// src/pages/qbo/SyncResultList.tsx
// Outcome of a push / conversion run: one line per record, errors first.

import { RefreshCw } from 'lucide-react';
import type { SyncDetail, SyncResult } from './qboTypes';
import { money } from './qboTypes';

function label(d: SyncDetail): string {
  if (d.offset) return `Offset ${d.docNumber || d.offset}`;
  return String(d.invoice || d.receipt || d.poNo || d.code || d.name || '');
}

export default function SyncResultList({ result, title = 'Result' }: { result: SyncResult | null; title?: string }) {
  if (!result) return null;
  const details = [...(result.details || [])].sort((a, b) => (a.status === 'error' ? -1 : 0) - (b.status === 'error' ? -1 : 0));
  return (
    <div className="mt-4 bg-slate-800 rounded-xl p-5 border border-slate-700">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-sm font-semibold text-slate-400 flex items-center gap-2">
          <RefreshCw size={14} /> {title}{result.duration ? ` (${result.duration})` : ''}
        </div>
        <div className="flex gap-3 text-xs">
          <span className="text-green-400">{result.created} created</span>
          <span className="text-blue-300">{result.updated} updated</span>
          <span className="text-slate-400">{result.skipped} skipped</span>
          <span className={result.errors ? 'text-red-400 font-semibold' : 'text-slate-500'}>{result.errors} errors</span>
        </div>
      </div>
      <div className="bg-slate-900 rounded-lg p-3 max-h-[360px] overflow-y-auto text-xs font-mono">
        {details.length === 0 && <div className="text-slate-500">Nothing to report</div>}
        {details.map((d, i) => {
          const ok = d.status === 'success' || d.status === 're-converted';
          const skipped = d.status === 'skipped';
          const info = d.status === 'info';
          const cls = ok ? 'text-green-400' : skipped ? 'text-slate-400' : info ? 'text-sky-300' : 'text-red-400';
          const amt = d.open ?? d.amount ?? d.total;
          return (
            <div key={i} className={`mb-1 ${cls}`}>
              {ok ? '✓' : skipped ? '–' : info ? 'ℹ' : '✗'} {label(d)}{d.customerName ? ` ${d.customerName}` : ''}
              {d.type ? ` [${d.type}]` : ''}
              {d.date ? ` ${d.date}` : ''}
              {typeof d.customer === 'string' && d.customer ? ` — ${d.customer}` : ''}
              {' — '}
              {ok
                ? `${d.action || 'ok'}${d.qboId ? ` → QBO ${d.qboId}` : ''}${amt !== undefined ? ` ($${money(amt)})` : ''}${d.documents ? ` for ${d.documents} document(s)` : ''}${d.cogsJournalId ? ` · COGS journal ${d.cogsJournalId}` : ''}${d.status === 're-converted' ? ` · stub rewritten (was $${money(d.was ?? 0)}, +$${money(d.delta ?? 0)} offset)` : ''}`
                : info ? d.note : d.error}
              {ok && d.warnings && d.warnings.length > 0 && (
                <span className="text-amber-300"> ⚠ {d.warnings.join('; ')}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
